// src/components/laboratory/forms/ProductForm.jsx - Revamped with categories
import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_PRODUCT, UPDATE_PRODUCT, GET_PRODUCT_SUBCATEGORIES } from '../../../api/laboratory-api';
import ModalComponent from '../../common/ModalComponent';
import { 
  Info, 
  X, 
  Image, 
  Plus, 
  Tag, 
  Package, 
  Upload,
  Video,
  Link,
  AlertCircle,
  Check
} from 'lucide-react';
import { toast } from 'react-toastify';
import { uploadProductVideo } from '../../../utils/imageUrl';
import {
  PRODUCT_CATEGORIES,
  getCategoryLabel,
} from '../../../constants/product-categories';

const ProductForm = ({ laboratory, product, restrictedEdit = false, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    prixPGHT: '',
    description: '',
    category: '',
    subcategory: '',
    image: null,
    imagePreview: null,
    videoLink: '',
    videoFile: null,       // File object for upload
    videoFileName: '',     // display name
    videoPath: '',         // path returned by backend after upload
    // For additional images
    additionalImage0: null,
    additionalImagePreview0: null,
    additionalImage1: null,
    additionalImagePreview1: null,
    additionalImage2: null,
    additionalImagePreview2: null,
    additionalImage3: null,
    additionalImagePreview3: null
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);

  // Fetch subcategories from database (managed in Settings > Catégories Produit)
  const { data: subcategoriesData } = useQuery(GET_PRODUCT_SUBCATEGORIES, {
    fetchPolicy: 'cache-and-network',
  });
  const allDbSubcategories = useMemo(() => subcategoriesData?.productSubcategories ?? [], [subcategoriesData]);

  // Initialize form data if editing an existing product
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        price: product.price ? String(product.price) : '',
        prixPGHT: product.prixPGHT ? String(product.prixPGHT) : '',
        description: product.description || '',
        category: product.category || '',
        subcategory: product.subcategory || '',
        imagePreview: product.image || null,
        videoLink: product.videoLink || '',
        videoFile: null,
        videoFileName: product.video ? product.video.split('/').pop() : '',
        videoPath: product.video || '',
        additionalImagePreview0: product.images || null,
        additionalImagePreview1: product.imagethree || null,
        additionalImagePreview2: product.quatriemimage || null,
        additionalImagePreview3: product.cinquiemimage || null
      });

      // Set subcategories based on the product's category (from DB)
      if (product.category) {
        const subs = allDbSubcategories
          .filter(s => s.category === product.category)
          .map(s => s.name);
        setAvailableSubcategories(subs);
      }
    } else {
      // Reset form for a new product
      setFormData({
        name: '',
        price: '',
        prixPGHT: '',
        description: '',
        category: '',
        subcategory: '',
        image: null,
        imagePreview: null,
        videoLink: '',
        videoFile: null,
        videoFileName: '',
        videoPath: '',
        additionalImage0: null,
        additionalImagePreview0: null,
        additionalImage1: null,
        additionalImagePreview1: null,
        additionalImage2: null,
        additionalImagePreview2: null,
        additionalImage3: null,
        additionalImagePreview3: null
      });
      setAvailableSubcategories([]);
    }
    setFormErrors({});
    setCurrentStep(1);
  }, [product, isOpen]);

  // Update available subcategories when category changes or DB data loads (never resets selection)
  useEffect(() => {
    if (formData.category) {
      const subcats = allDbSubcategories
        .filter(s => s.category === formData.category)
        .map(s => s.name);
      setAvailableSubcategories(subcats);
    } else {
      setAvailableSubcategories([]);
    }
  }, [formData.category, allDbSubcategories]);

  // Mutations
  const [createProduct] = useMutation(CREATE_PRODUCT, {
    onCompleted: (data) => {
      if (data.createProduct) {
        toast.success('Produit créé avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la création du produit');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la création du produit: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const [updateProduct] = useMutation(UPDATE_PRODUCT, {
    onCompleted: (data) => {
      if (data.updateProduct) {
        toast.success('Produit mis à jour avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la mise à jour du produit');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour du produit: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Image handlers
  const handleProductImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner un fichier image valide');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImageChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner un fichier image valide');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [`additionalImage${index}`]: file,
          [`additionalImagePreview${index}`]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Video file handler
  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = 200 * 1024 * 1024; // 200 MB
    if (file.size > maxSize) { toast.error('La vidéo ne doit pas dépasser 200 MB'); return; }
    setFormData(prev => ({ ...prev, videoFile: file, videoFileName: file.name, videoPath: '' }));
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, videoFile: null, videoFileName: '', videoPath: '' }));
  };

  // Form validation
  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      // Basic information validation
      if (!restrictedEdit) {
        if (!formData.name.trim()) {
          errors.name = 'Le nom du produit est requis';
        } else if (formData.name.length < 3) {
          errors.name = 'Le nom doit contenir au moins 3 caractères';
        }

        if (!formData.price || isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
          errors.price = 'Le prix doit être un nombre positif';
        } else if (formData.prixPGHT && parseFloat(formData.price) < parseFloat(formData.prixPGHT)) {
          errors.price = 'Le prix public ne peut pas être inférieur au prix PGHT';
        }
      }

      if (!formData.description.trim()) {
        errors.description = 'La description du produit est requise';
      } else if (formData.description.length < 10) {
        errors.description = 'La description doit contenir au moins 10 caractères';
      }
    }

    if (step === 2) {
      // Category validation (optional but recommended)
      if (!formData.category) {
        errors.category = 'Il est recommandé de sélectionner une catégorie';
      }
    }

    if (step === 3) {
      // Media validation
      if (formData.videoLink && !isValidUrl(formData.videoLink)) {
        errors.videoLink = 'Veuillez entrer une URL valide';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    return validateStep(1) && validateStep(2) && validateStep(3);
  };

  // URL validation helper
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Step navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Form submission handler
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Upload video file if a new one was selected
      let videoPath = formData.videoPath || null;
      if (formData.videoFile) {
        try {
          videoPath = await uploadProductVideo(formData.videoFile);
        } catch (uploadErr) {
          toast.error(`Erreur upload vidéo : ${uploadErr.message}`);
          setIsSubmitting(false);
          return;
        }
      }

      // Prepare product data for submission
      const productInput = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        prixPGHT: formData.prixPGHT ? parseFloat(formData.prixPGHT) : undefined,
        description: formData.description.trim(),
        category: formData.category || undefined,
        subcategory: formData.subcategory || undefined,
        laboratoryId: laboratory.id,
        videoLink: formData.videoLink || undefined,
        video: videoPath || undefined,
      };

      // In a real application, we would upload images to a server here
      // and get back URLs to use in the product data
      if (formData.imagePreview) {
        productInput.image = formData.imagePreview;
      }

      // Add additional images if present
      for (let i = 0; i < 4; i++) {
        const preview = formData[`additionalImagePreview${i}`];
        if (preview) {
          const fieldName = i === 0 ? 'images' :
            i === 1 ? 'imagethree' :
              i === 2 ? 'quatriemimage' : 'cinquiemimage';
          productInput[fieldName] = preview;
        }
      }

      if (product) {
        // Update existing product
        productInput.id = product.id;
        await updateProduct({
          variables: {
            updateProductInput: productInput
          }
        });
      } else {
        // Create new product
        await createProduct({
          variables: {
            createProductInput: productInput
          }
        });
      }
      
      // Success is handled in the mutation callbacks
    } catch (error) {
      console.error('Error saving product:', error);
      setIsSubmitting(false);
    }
  };

  // Remove image handler
  const removeImage = (type, index = null) => {
    if (type === 'main') {
      setFormData(prev => ({
        ...prev,
        image: null,
        imagePreview: null
      }));
    } else {
      setFormData(prev => {
        const updated = { ...prev };
        delete updated[`additionalImage${index}`];
        delete updated[`additionalImagePreview${index}`];
        return updated;
      });
    }
  };

  // Render step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
            currentStep >= step 
              ? 'bg-green-600 border-green-600 text-white' 
              : 'border-gray-300 text-gray-500'
          }`}>
            {currentStep > step ? <Check size={16} /> : step}
          </div>
          {step < 3 && (
            <div className={`w-16 h-0.5 ${
              currentStep > step ? 'bg-green-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Render step 1: Basic Information
  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="text-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Informations de base</h3>
        <p className="text-sm text-gray-500">Renseignez les informations principales du produit</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom du produit {!restrictedEdit && <span className="text-red-500">*</span>}
          </label>
          {restrictedEdit ? (
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
              {formData.name}
            </div>
          ) : (
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: Doliprane 1000mg, Crème hydratante..."
            />
          )}
          {formErrors.name && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {formErrors.name}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prix PGHT (DH) {!restrictedEdit && <span className="text-red-500">*</span>}
          </label>
          <p className="text-xs text-gray-400 mb-1">Prix d'achat pharmacien hors taxes</p>
          <div className="relative">
            {restrictedEdit ? (
              <div className="w-full px-3 py-2 pr-12 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                {formData.prixPGHT || '—'}
                <span className="absolute right-3 top-2 text-gray-500">DH</span>
              </div>
            ) : (
              <input
                type="number"
                name="prixPGHT"
                value={formData.prixPGHT}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.prixPGHT ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0.00"
              />
            )}
            {!restrictedEdit && <span className="absolute right-3 top-2 text-gray-500">DH</span>}
          </div>
          {formErrors.prixPGHT && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {formErrors.prixPGHT}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prix public (DH) {!restrictedEdit && <span className="text-red-500">*</span>}
          </label>
          <p className="text-xs text-gray-400 mb-1">Prix de vente au consommateur</p>
          <div className="relative">
            {restrictedEdit ? (
              <div className="w-full px-3 py-2 pr-12 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                {formData.price}
                <span className="absolute right-3 top-2 text-gray-500">DH</span>
              </div>
            ) : (
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.price ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0.00"
              />
            )}
            {!restrictedEdit && <span className="absolute right-3 top-2 text-gray-500">DH</span>}
          </div>
          {formErrors.price && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {formErrors.price}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
              formErrors.description ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Description détaillée du produit, composition, indications, posologie..."
          />
          {formErrors.description && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {formErrors.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Render step 2: Categories
  const renderStep2 = () => (
    <div className="space-y-5">
      <div className="text-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Catégorisation</h3>
        <p className="text-sm text-gray-500">Classez votre produit pour faciliter sa recherche</p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Catégorie principale
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PRODUCT_CATEGORIES.map((category) => (
              <div
                key={category.key}
                className={`relative cursor-pointer rounded-lg border p-3 hover:bg-gray-50 ${
                  formData.category === category.key
                    ? 'border-green-500 bg-green-50 ring-2 ring-green-500'
                    : 'border-gray-300'
                }`}
                onClick={() => setFormData(prev => ({ ...prev, category: category.key, subcategory: '' }))}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="category"
                    value={category.key}
                    checked={formData.category === category.key}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex items-center space-x-2">
                    {category.icon}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {category.label}
                      </p>
                      <p className="text-xs text-gray-500 line-clamp-2">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  {formData.category === category.key && (
                    <Check className="absolute top-2 right-2 h-4 w-4 text-green-600" />
                  )}
                </div>
              </div>
            ))}
          </div>
          {formErrors.category && (
            <p className="mt-1 text-sm text-yellow-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {formErrors.category}
            </p>
          )}
        </div>

        {formData.category && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sous-catégorie (optionnel)
            </label>
            {availableSubcategories.length > 0 ? (
              <select
                name="subcategory"
                value={formData.subcategory}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Sélectionner une sous-catégorie</option>
                {availableSubcategories.map((subcat) => (
                  <option key={subcat} value={subcat}>{subcat}</option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-gray-400 italic">
                Aucune sous-catégorie disponible. Ajoutez-en dans Paramètres → Catégories Produit.
              </p>
            )}
          </div>
        )}

        {formData.category && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Catégorie sélectionnée :</p>
                <p className="flex items-center">
                  {formData.category.icon}
                  <span className="ml-2">{getCategoryLabel(formData.category)}</span>
                  {formData.subcategory && (
                    <span className="ml-2 text-blue-600">
                      → {formData.subcategory}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render step 3: Media
  const renderStep3 = () => (
    <div className="space-y-5">
      <div className="text-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Images et médias</h3>
        <p className="text-sm text-gray-500">Ajoutez des images pour présenter votre produit</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Main Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image principale
          </label>
          <div className="space-y-3">
            {formData.imagePreview ? (
              <div className="relative">
                <img
                  src={formData.imagePreview}
                  alt="Product preview"
                  className="w-full h-48 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => removeImage('main')}
                  className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div
                className="w-full h-48 border-2 border-gray-300 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 hover:border-gray-400 cursor-pointer transition-colors"
                onClick={() => document.getElementById('product-image-upload').click()}
              >
                <Upload className="h-8 w-8 mb-2" />
                <p className="text-sm font-medium">Cliquer pour ajouter une image</p>
                <p className="text-xs">PNG, JPG jusqu'à 5MB</p>
              </div>
            )}
            <input
              id="product-image-upload"
              name="image"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProductImageChange}
            />
          </div>
        </div>

        {/* Video section */}
        <div className="md:col-span-2 space-y-3">
          <label className="block text-sm font-medium text-gray-700">Vidéo (optionnel)</label>

          {/* Toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {[
              { key: 'link', label: 'Lien YouTube / URL' },
              { key: 'file', label: 'Fichier vidéo' },
            ].map(({ key, label }) => {
              const isSelected = key === 'file' ? !!formData.videoFileName : !formData.videoFileName;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    if (key === 'link') {
                      removeVideo();
                    } else {
                      setFormData(prev => ({ ...prev, videoLink: '' }));
                      document.getElementById('product-video-upload').click();
                    }
                  }}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${
                    isSelected ? 'bg-green-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Link input */}
          {!formData.videoFileName && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Video className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                name="videoLink"
                value={formData.videoLink || ''}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.videoLink ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              {formErrors.videoLink && (
                <p className="text-sm text-red-600 flex items-center mt-1">
                  <AlertCircle size={14} className="mr-1" />
                  {formErrors.videoLink}
                </p>
              )}
            </div>
          )}

          {/* File display */}
          {formData.videoFileName && (
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
              <Video className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="flex-1 text-sm text-green-800 truncate">{formData.videoFileName}</span>
              <button type="button" onClick={removeVideo} className="text-red-500 hover:text-red-700 flex-shrink-0">
                <X size={16} />
              </button>
            </div>
          )}

          <input
            id="product-video-upload"
            type="file"
            accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm"
            className="hidden"
            onChange={handleVideoFileChange}
          />
        </div>
      </div>

      {/* Additional Images */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Images supplémentaires (max 4)
        </label>
        <div className="grid grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="relative">
              {formData[`additionalImagePreview${index}`] ? (
                <>
                  <img
                    src={formData[`additionalImagePreview${index}`]}
                    alt={`Additional image ${index + 1}`}
                    className="w-full h-24 object-cover rounded-md border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage('additional', index)}
                    className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </>
              ) : (
                <div
                  className="w-full h-24 border-2 border-gray-300 border-dashed rounded-md flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 hover:border-gray-400 cursor-pointer transition-colors"
                  onClick={() => document.getElementById(`additional-image-${index}`).click()}
                >
                  <Plus className="h-5 w-5" />
                  <input
                    id={`additional-image-${index}`}
                    name={`additionalImage${index}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleAdditionalImageChange(e, index)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Custom footer with step navigation
  const renderFooter = () => (
    <div className="flex items-center justify-between pt-4 border-t">
      <div className="flex items-center text-sm text-gray-500">
        <Package className="h-4 w-4 mr-1" />
        Laboratoire: <strong className="ml-1">{laboratory?.nom}</strong>
      </div>
      
      <div className="flex items-center space-x-3">
        {currentStep > 1 && (
          <button
            type="button"
            onClick={prevStep}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Précédent
          </button>
        )}
        
        {currentStep < 3 ? (
          <button
            type="button"
            onClick={nextStep}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Suivant
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {product ? 'Mise à jour...' : 'Création...'}
              </div>
            ) : (
              product ? 'Mettre à jour' : 'Créer le produit'
            )}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={product ? (restrictedEdit ? 'Modifier le produit (promotion active)' : 'Modifier le produit') : 'Ajouter un nouveau produit'}
      size="xl"
      showFooter={false}
    >
      <div className="space-y-6">
        <StepIndicator />
        
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        
        {renderFooter()}
      </div>
    </ModalComponent>
  );
};

export default ProductForm;